import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatMoney } from "src/util/gFormatting";
import { tAbandonedCartReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const getAbandonedCartReport = (start: Date, end: Date) =>
  api.get<tAbandonedCartReport[]>("reports/abandoned-carts", getParams(start, end)).then((res) => res.data);

const columns: tColumn<tAbandonedCartReport>[] = [
  {
    id: "retailerAccountManagerName",
    header: "Account Manager",
    accessor: (originalRow) => originalRow.retailerAccountManagerName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "dateCreatedInSeconds",
    header: "Date",
    accessor: (originalRow) => (originalRow.dateCreatedInSeconds ? formatDate(originalRow.dateCreatedInSeconds) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "retailerName",
    header: "Retailer Name",
    accessor: (originalRow) => originalRow.retailerName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "cartGrossAmount",
    header: "Cart Subtotal",
    accessor: (originalRow) => formatMoney(originalRow.cartSubtotalAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "cartTotalAmount",
    header: "Cart Total",
    accessor: (originalRow) => formatMoney(originalRow.cartTotalAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "salesRepName",
    header: "Sales Rep",
    accessor: (originalRow) => originalRow.salesRepName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "source",
    header: "Source",
    accessor: (originalRow) =>
      originalRow.draftSource === "brand" ? "Brand" : originalRow.draftSource === "rep" ? "Sales Rep" : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
];

const AbandonedCartsTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.abandonedCartsReport);
  const { isLoading, data } = useFocusableQuery<tAbandonedCartReport[]>(
    useCallback(() => getAbandonedCartReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => item.id}
      sortedBy="dateCreatedInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="abandonedCartsReport"
    />
  );
};

export default AbandonedCartsTable;
